import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, MessageCircle, X } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { useAuth } from '../App';

const API = '/api';

const formatTimeAgo = (dateString) => {
  if (!dateString) return '';
  const diff = Date.now() - new Date(dateString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(diff / 3600000);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(diff / 86400000);
  if (days < 7) return `${days}d`;
  return new Date(dateString).toLocaleDateString();
};

const MessagesInboxPanel = ({ open, onClose, onOpenChat }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchTimerRef = useRef(null);
  const panelRef = useRef(null);

  const fetchContacts = useCallback(async (query = '') => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ limit: '30' });
      if (query) params.set('q', query);
      const res = await axios.get(`${API}/messages/contacts?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setContacts(res.data.contacts || []);
    } catch {
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchContacts();
  }, [open, fetchContacts]);

  // Click-outside handler
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, onClose]);

  // Debounced search
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => fetchContacts(value), 200);
  };

  useEffect(() => {
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, []);

  const handleContactClick = (contact) => {
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      // Mobile: navigate to full-screen messages
      if (contact.conversation_id) {
        navigate(`/messages?conversation=${contact.conversation_id}`);
      } else {
        // Create conversation then navigate
        const token = localStorage.getItem('token');
        axios.post(`${API}/conversations/with`, { other_user_id: contact.user_id }, {
          headers: { Authorization: `Bearer ${token}` },
        }).then((res) => {
          navigate(`/messages?conversation=${res.data.conversation_id}`);
        }).catch(() => {
          navigate('/messages');
        });
      }
      onClose();
    } else {
      // Desktop: open floating chat
      onOpenChat(contact);
      onClose();
    }
  };

  if (!open) return null;

  const recentContacts = contacts.filter((c) => c.source === 'recent');
  const followingContacts = contacts.filter((c) => c.source === 'following');

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-full mt-2 w-[340px] sm:w-[380px] bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl overflow-hidden z-50"
      onClick={(e) => e.stopPropagation()}
      data-testid="messages-inbox-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-700">
        <h3 className="font-bold text-white text-sm">Messages</h3>
        <button onClick={onClose} className="text-zinc-400 hover:text-white transition-colors" data-testid="inbox-close-btn">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Search */}
      <div className="px-3 py-2 border-b border-zinc-800">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search creators..."
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-600"
            data-testid="inbox-search-input"
          />
        </div>
      </div>

      {/* Contact list */}
      <div className="max-h-[400px] overflow-y-auto" data-testid="inbox-contact-list">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500 mx-auto" />
          </div>
        ) : contacts.length === 0 ? (
          <div className="p-8 text-center">
            <MessageCircle className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
            <p className="text-zinc-500 text-sm">
              {searchQuery ? 'No creators found' : 'Follow creators to start messaging'}
            </p>
          </div>
        ) : (
          <>
            {/* Recent conversations */}
            {recentContacts.length > 0 && (
              <>
                {!searchQuery && (
                  <div className="px-4 pt-3 pb-1">
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Recent</p>
                  </div>
                )}
                {recentContacts.map((contact) => (
                  <ContactRow key={contact.user_id} contact={contact} onClick={() => handleContactClick(contact)} />
                ))}
              </>
            )}

            {/* Following contacts */}
            {followingContacts.length > 0 && (
              <>
                {!searchQuery && (
                  <div className="px-4 pt-3 pb-1">
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Following</p>
                  </div>
                )}
                {followingContacts.map((contact) => (
                  <ContactRow key={contact.user_id} contact={contact} onClick={() => handleContactClick(contact)} />
                ))}
              </>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-zinc-700 px-4 py-2.5">
        <button
          onClick={() => { onClose(); navigate('/messages'); }}
          className="text-xs text-blue-400 hover:text-blue-300 transition-colors w-full text-center"
          data-testid="inbox-view-all"
        >
          View all messages
        </button>
      </div>
    </div>
  );
};

const ContactRow = ({ contact, onClick }) => {
  const hasUnread = (contact.unread_count || 0) > 0;
  const lastMsg = contact.last_message;
  const preview = lastMsg?.content || lastMsg?.text || '';

  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-zinc-800 transition-colors text-left ${hasUnread ? 'bg-zinc-800/40' : ''}`}
      data-testid={`contact-row-${contact.user_id}`}
    >
      <Avatar className="h-9 w-9 flex-shrink-0">
        {contact.avatar_url && <AvatarImage src={contact.avatar_url} alt={contact.display_name} />}
        <AvatarFallback className="bg-gradient-to-br from-orange-500 to-red-500 text-white text-xs font-bold">
          {(contact.display_name || contact.handle || 'U')[0].toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <p className={`text-sm truncate ${hasUnread ? 'font-semibold text-white' : 'text-zinc-300'}`}>
            {contact.display_name || contact.handle || 'Creator'}
          </p>
          {contact.last_message_at && (
            <span className="text-[10px] text-zinc-500 flex-shrink-0 ml-2">
              {formatTimeAgo(contact.last_message_at)}
            </span>
          )}
        </div>
        <p className={`text-xs truncate mt-0.5 ${hasUnread ? 'text-zinc-300' : 'text-zinc-500'}`}>
          {preview || (contact.handle ? `@${contact.handle}` : 'Start a conversation')}
        </p>
      </div>
      {hasUnread && (
        <span className="flex-shrink-0 bg-orange-500 text-white text-[10px] rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
          {contact.unread_count > 9 ? '9+' : contact.unread_count}
        </span>
      )}
    </button>
  );
};

export default MessagesInboxPanel;
